/**
 * NOTE: This file can have cycles added to it, but cycles SHOULD NEVER be removed.
 * This file is used to maintain treatments since the first treatment was applied.
 */
import {ICycle} from 'core/api/prescription'
import {deepFreeze} from 'core/utils/freeze'
import {minutesToMs} from 'core/utils/time-helpers'
import {noop} from 'lodash'
import {TreatmentTemplates} from '../treatments'
import {CYCLE_CAM1D_2_CONST_CONTRAST} from './cam1d-2'
import * as DichopticMasks from './dichoptic-masks'

export {CYCLE_CAM1D_2_CONST_CONTRAST} from './cam1d-2'

/**
 * Primary treatment cycle used in C-AM-1A
 */
const CYCLE_1: ICycle = {
  name: 'CYCLE_1',
  treatments: [
    {...TreatmentTemplates.MONOCULAR_CONTRAST__1, duration: 30 * 60 * 1000},
    {...TreatmentTemplates.MONOCULAR_FLICKER__1, duration: 20 * 60 * 1000},
    {...TreatmentTemplates.BINOCULAR_FLICKER__1, duration: 10 * 60 * 1000},
  ],
  assets: {},
}

/**
 * Demonstration cycle that rotates through every treatment type
 * in a rapid manner
 */
const CYCLE_DEMO_A: ICycle = {
  name: 'CYCLE_DEMO_A',
  treatments: [
    {...TreatmentTemplates.MONOCULAR_CONTRAST__1, duration: 1 * 60 * 1000},
    {...TreatmentTemplates.MONOCULAR_FLICKER__1, duration: 1 * 60 * 1000},
    {...TreatmentTemplates.BINOCULAR_FLICKER__1, duration: 1 * 60 * 1000},
    {...TreatmentTemplates.DICHOPTIC_PRESENTATION__1, duration: 1 * 60 * 1000},
  ],
  assets: {
    dichopticMasks: DichopticMasks.DEMO_DICHOPTIC_MASKS,
  },
}

/**
 * Testing cycle which will leverage the normal treatment pipeline but
 * have no treatment effect shown.
 */
const CYCLE_PLACEBO: ICycle = {
  name: 'CYCLE_PLACEBO',
  treatments: [
    {...TreatmentTemplates.PLACEBO__1, duration: minutesToMs(60)},
  ],
  assets: {},
}

/**
 * Primary treatment cycle used in C-AM-1C
 *
 * Created according to Scott Xiao on 2017-06-13
 */
const CYCLE_CAM1C: ICycle = {
  name: 'CYCLE_CAM1C',
  treatments: [
    {...TreatmentTemplates.DICHOPTIC_PRESENTATION__CAM1C_1, duration: minutesToMs(60)},
  ],
  assets: {
    dichopticMasks: DichopticMasks.CAM1C_DICHOPTIC_MASKS,
  },
}

/**
 * Constant contrast treatment cycle used in C-AM-1D. Same treatment
 * as C-AM-1C but with the new dichoptic masks.
 *
 * Created according to Scott Xiao on 2017-10-03
 */
const CYCLE_CAM1D_CONST_CONTRAST: ICycle = {
  name: 'CYCLE_CAM1D_CONST_CONTRAST',
  treatments: [
    {...TreatmentTemplates.DICHOPTIC_PRESENTATION__CAM1C_1, duration: minutesToMs(60)},
  ],
  assets: {
    dichopticMasks: DichopticMasks.CAM1D_DICHOPTIC_MASKS,
  },
}

/**
 * Incrementing contrast treatment cycle used in C-AM-1D. Leverages
 * the incrementing contrast function and the new dichoptic masks.
 *
 * Created according to Scott Xiao on 2017-10-03
 */
const CYCLE_CAM1D_INC_CONTRAST: ICycle = {
  name: 'CYCLE_CAM1D_INC_CONTRAST',
  treatments: [
    {...TreatmentTemplates.BINOCULAR_INC_CONTRAST__CAM1D_1, duration: minutesToMs(60)},
  ],
  assets: {
    dichopticMasks: DichopticMasks.CAM1D_DICHOPTIC_MASKS,
  },
}

export type Cycle =
  | typeof CYCLE_1
  | typeof CYCLE_DEMO_A
  | typeof CYCLE_PLACEBO
  | typeof CYCLE_CAM1C
  | typeof CYCLE_CAM1D_CONST_CONTRAST
  | typeof CYCLE_CAM1D_INC_CONTRAST

/**
 * All available Cycles.
 * All Cycles should be exported through this object, and never imported directly.
 * This ensures that integration tests are run on all Cycles.
 *
 * BAD: `import {CYCLE_FOO} from './path/to/cycles'`
 * GOOD: `import {Cycles} from './path/to/cycles'; Cycles.CYCLE_FOO`
 */
export const Cycles = {
  CYCLE_1,
  CYCLE_DEMO_A,
  CYCLE_PLACEBO,
  CYCLE_CAM1C,
  CYCLE_CAM1D_CONST_CONTRAST,
  CYCLE_CAM1D_INC_CONTRAST,
  CYCLE_CAM1D_2_CONST_CONTRAST,
}
deepFreeze(Cycles)

// Type-check for Cycles
const cyclesTest: {[key: string]: ICycle} = Cycles
noop(cyclesTest)



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./core/src/prescription/treatment/cycles/index.ts